/**
 * Safely extracts the hostname from a URL string
 * @param url - The URL string to parse
 * @returns The hostname, or null if parsing fails
 */
export const getHostname = (url: string): string | null => {
  try {
    // Handle relative URLs by prepending a base URL
    if (!url.includes("://")) {
      // For relative URLs, we can't extract a meaningful hostname
      return null;
    }
    return new URL(url).hostname;
  }
  catch {
    // Return null for invalid URLs instead of the original string
    return null;
  }
};

/**
 * Checks if a URL points to a PDF file
 * @param url - The URL string to check
 * @returns True if the URL ends with .pdf (case insensitive)
 */
export const isPdfUrl = (url: string): boolean => {
  return url.toLowerCase().endsWith(".pdf");
};

/**
 * Safe URL transform for React Markdown that allows safe protocols
 * while blocking dangerous ones like javascript:, data:, vbscript:, file:
 * @param url - The URL to validate and transform
 * @returns The URL if safe, empty string if dangerous
 */
export const safeUrlTransform = (url: string): string => {
  // Safe protocols: http(s), mailto, tel
  const safeProtocolRegex = /^(?:https?|mailto|tel):/i;

  // More robust relative URL detection using regex
  // Relative URLs: start with /, ./, ../, or have no protocol
  const relativeUrlRegex = /^(?:\/|\.\/|\.\.\/|[^:/?#]+\/)/;

  // Check if URL has a protocol by looking for scheme at the start
  const hasProtocol = /^[a-z][a-z0-9+.-]*:/i.test(url);

  const isRelative = !hasProtocol || relativeUrlRegex.test(url);

  // Allow safe protocols and relative URLs
  if (isRelative || safeProtocolRegex.test(url)) {
    return url;
  }

  // Block dangerous protocols (javascript:, data:, vbscript:, file:, etc.)
  console.warn("Blocked potentially unsafe URL:", url);
  return "";
};
